import { useState, useEffect, useCallback, RefObject, useRef } from 'react';
import { AspectRatio } from '../types';
import { LAYOUT_CONSTANTS } from '../constants/layout';

interface UseSlideScalingProps {
  aspectRatio: AspectRatio;
  containerRef: RefObject<HTMLDivElement>;
  isEditMode?: boolean;
  displayScale?: number; // 표시 크기 배율 (50-150%)
  useActualChildSize?: boolean; // 실제 자식 요소 크기 사용 여부
  isRightPanelOpen?: boolean; // 우측 패널 열림 여부
}

interface SlideScalingResult {
  scale: number;
  containerStyle: React.CSSProperties;
  actualWidth: number;
  actualHeight: number;
}

export const useSlideScaling = ({
  aspectRatio,
  containerRef,
  isEditMode = false,
  displayScale = 100,
  useActualChildSize = false,
  isRightPanelOpen = false
}: UseSlideScalingProps): SlideScalingResult => {
  const [scale, setScale] = useState(1);
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  
  // 고정된 크기를 저장하는 ref
  const fixedSizeRef = useRef<{ width: number; height: number } | null>(null);
  // 마지막 aspectRatio를 저장하는 ref
  const lastAspectRatioRef = useRef<AspectRatio>(aspectRatio);
  // 마지막 displayScale을 저장하는 ref
  const lastDisplayScaleRef = useRef<number>(displayScale);

  const calculateDimensions = useCallback(() => {
    const aspectRatioValue = aspectRatio.width / aspectRatio.height;
    
    let width, height;
    
    if (useActualChildSize && containerRef.current) {
      // 고정된 크기가 있고 aspectRatio와 displayScale이 변경되지 않았으면 그것을 사용
      if (fixedSizeRef.current && 
          lastAspectRatioRef.current.width === aspectRatio.width && 
          lastAspectRatioRef.current.height === aspectRatio.height &&
          lastDisplayScaleRef.current === displayScale) {
        width = fixedSizeRef.current.width;
        height = fixedSizeRef.current.height;
        return { width, height };
      }
      
      // 실제 slide-renderer-container의 크기를 가져와서 사용
      const slideRenderer = containerRef.current.querySelector('.slide-renderer-container') as HTMLElement;
      
      if (slideRenderer) {
        const computedStyle = window.getComputedStyle(slideRenderer);
        
        // 실제 크기 (transform scale 제외)
        const actualWidth = parseFloat(computedStyle.width);
        const actualHeight = parseFloat(computedStyle.height);
        
        // displayScale 적용
        const scaleMultiplier = displayScale / 100;
        
        // slide-renderer-container와 동일한 크기로 설정 (여백 없이)
        width = actualWidth * scaleMultiplier;
        height = actualHeight * scaleMultiplier;
        
        // 최소 크기 보장
        const minWidth = 250;
        const minHeight = minWidth / aspectRatioValue;
        
        if (width < minWidth) {
          width = minWidth;
          height = minHeight;
        }
        
        if (height < minHeight) {
          height = minHeight;
          width = height * aspectRatioValue;
        }
        
        // 크기를 고정값으로 저장하고 현재 설정들도 저장
        fixedSizeRef.current = { width, height };
        lastAspectRatioRef.current = { ...aspectRatio };
        lastDisplayScaleRef.current = displayScale;
        
        return { width, height };
      }
    }
    
    // 기존 로직: 브라우저 크기 기반으로 동적 조정
    // 모바일에서는 사이드패널이 숨겨지므로 전체 폭 사용
    const isMobile = window.innerWidth < 768; // md 브레이크포인트
    const sidebarWidth = isMobile ? 0 : LAYOUT_CONSTANTS.SIDEBAR_WIDTH; // 모바일에서는 사이드바 없음
    // 우측 패널은 모바일에서 모달이므로 너비에서 제외
    const rightPanelWidth = (isRightPanelOpen && !isMobile) ? LAYOUT_CONSTANTS.RIGHT_PANEL_WIDTH : 0;
    
    const mainContentWidth = window.innerWidth - sidebarWidth - rightPanelWidth;
    const toolbarHeight = LAYOUT_CONSTANTS.TOTAL_TOP_HEIGHT; // 상단 헤더 + 편집 툴바 높이
    
    // 모바일에서는 더 많은 공간 활용
    const baseWidth = mainContentWidth * (isMobile ? 0.98 : 0.9); // 모바일 98%, 데스크톱 90%
    const baseHeight = (window.innerHeight - toolbarHeight) * (isMobile ? 0.95 : 0.9); // 모바일 95%, 데스크톱 90%
    
    // displayScale 적용 (50% ~ 150%)
    const scaleMultiplier = displayScale / 100;
    const availableWidth = baseWidth * scaleMultiplier;
    const availableHeight = baseHeight * scaleMultiplier;
    
    // 화면 크기에 맞춰 조정하되, 비율을 정확히 유지
    if (availableWidth / availableHeight > aspectRatioValue) {
      // 세로가 제약이므로 높이를 기준으로 조정
      height = availableHeight;
      width = height * aspectRatioValue;
      
      // 너비가 제한을 초과하면 너비 기준으로 재조정
      if (width > availableWidth) {
        width = availableWidth;
        height = width / aspectRatioValue;
      }
    } else {
      // 가로가 제약이므로 너비를 기준으로 조정  
      width = availableWidth;
      height = width / aspectRatioValue;
      
      // 높이가 제한을 초과하면 높이 기준으로 재조정
      if (height > availableHeight) {
        height = availableHeight;
        width = height * aspectRatioValue;
      }
    }
    
    // 최소 크기 보장하되 비율 유지 (모바일/데스크톱 분리)
    const minWidthBase = isMobile 
      ? LAYOUT_CONSTANTS.MOBILE_MIN_SLIDE_WIDTH 
      : LAYOUT_CONSTANTS.MIN_SLIDE_WIDTH;
    // 모바일에서는 더 작은 최소값 사용하여 화면을 최대한 활용
    const minWidth = isMobile 
      ? Math.min(minWidthBase, availableWidth * 0.6) // 모바일: 기본 최소값과 60% 중 작은 값
      : Math.max(minWidthBase, availableWidth * 0.3); // 데스크톱: 기존 로직 유지
    const minHeight = minWidth / aspectRatioValue;
    
    // 최대 크기 제한 (화면을 벗어나지 않도록)
    const maxWidth = availableWidth;
    const maxHeight = availableHeight;
    
    if (width < minWidth) {
      width = minWidth;
      height = minHeight;
    }
    
    if (width > maxWidth) {
      width = maxWidth;
      height = width / aspectRatioValue;
    }
    
    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspectRatioValue;
    }
    
    return { width, height };
  }, [aspectRatio.width, aspectRatio.height, isEditMode, displayScale, useActualChildSize, containerRef, isRightPanelOpen]);

  const updateScale = useCallback(() => {
    if (containerRef.current) {
      const { width, height } = calculateDimensions();
      // Calculate scale based on the container size vs aspect ratio size
      const scaleX = width / aspectRatio.width;
      const scaleY = height / aspectRatio.height;
      // Use the smaller scale to ensure the slide fits within the container
      const currentScale = Math.min(scaleX, scaleY);
      // 비율별 보정 계수 적용
      const aspectRatioValue = aspectRatio.width / aspectRatio.height;
      let adjustedScale = currentScale;

      if (aspectRatioValue > 1) { // 21:9 같은 초광대형
        adjustedScale = currentScale * 1.5; // 50% 증가
      } else if (aspectRatioValue < 0.6) { // 9:16, 10:16 같은 세로형
        adjustedScale = currentScale * 1.5; // 50% 증가
      }
      setScale(adjustedScale);
      setContainerDimensions({ width, height });
    }
  }, [aspectRatio.width, aspectRatio.height, calculateDimensions, containerRef]);

  useEffect(() => {
    // 디바운스된 리사이즈 핸들러
    let resizeTimeout: NodeJS.Timeout;
    let visibilityTimeout: NodeJS.Timeout;
    let animationFrame: number;
    
    const debouncedUpdateScale = () => {
      clearTimeout(resizeTimeout);
      cancelAnimationFrame(animationFrame);
      
      // 즉시 반응성을 위해 animationFrame 사용
      animationFrame = requestAnimationFrame(() => {
        updateScale();
      });
      
      // 백업으로 setTimeout도 유지
      resizeTimeout = setTimeout(() => {
        updateScale();
      }, 100); // 더 빠른 반응을 위해 100ms로 줄임
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // 탭이 다시 활성화될 때 스케일 재계산
        clearTimeout(visibilityTimeout);
        visibilityTimeout = setTimeout(() => {
          updateScale();
        }, 50);
      }
    };

    // F11 전체화면 모드 감지
    const handleFullscreenChange = () => {
      // 전체화면 변경 시 즉시 리사이즈
      setTimeout(() => {
        updateScale();
      }, 100);
    };

    updateScale();
    window.addEventListener('resize', debouncedUpdateScale);
    window.addEventListener('orientationchange', debouncedUpdateScale);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearTimeout(resizeTimeout);
      clearTimeout(visibilityTimeout);
      cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', debouncedUpdateScale);
      window.removeEventListener('orientationchange', debouncedUpdateScale);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [updateScale]);

  // aspectRatio가 변경될 때만 고정된 크기 초기화 (실제로 변경되었을 때만)
  useEffect(() => {
    if (lastAspectRatioRef.current.width !== aspectRatio.width || 
        lastAspectRatioRef.current.height !== aspectRatio.height) {
      fixedSizeRef.current = null;
      lastAspectRatioRef.current = { ...aspectRatio };
      // 즉시 스케일 업데이트 트리거
      updateScale();
    }
  }, [aspectRatio, updateScale]);

  // displayScale이 변경될 때 고정된 크기 초기화 (실제로 변경되었을 때만)
  useEffect(() => {
    if (lastDisplayScaleRef.current !== displayScale) {
      fixedSizeRef.current = null;
      lastDisplayScaleRef.current = displayScale;
      // 즉시 스케일 업데이트 트리거
      updateScale();
    }
  }, [displayScale, updateScale]);

  const getContainerStyle = (): React.CSSProperties => {
    const { width, height } = calculateDimensions();
    
    // 편집 모드와 미리보기 모드에 따라 다른 크기 적용
    return {
      width: `${width}px`,
      height: `${height}px`,
      maxWidth: `${width}px`,
      maxHeight: `${height}px`,
      margin: '0',
      position: 'relative' as const,
      overflow: 'hidden',
      flexShrink: 0
    };
  };

  const { width: calculatedWidth, height: calculatedHeight } = calculateDimensions();
  
  return {
    scale,
    containerStyle: getContainerStyle(),
    actualWidth: calculatedWidth, // 실제 계산된 크기 사용
    actualHeight: calculatedHeight // 실제 계산된 크기 사용
  };
};